'use client';

import type { Batch, PaymentMethod, ReferralWithdrawal, User } from '@/api/entities';
import type { ReferralResponse } from '@/api/user';
import { useProfileData } from '@/hooks/query-hooks/use-get-profile';
import React, { createContext, useContext } from 'react';
interface ProfileContextType {
  user: User | undefined;
  paymentMethods: PaymentMethod | undefined;
  purchasedBatches: Batch[] | undefined;
  referrals?: ReferralResponse | undefined;
  withdrawals?: ReferralWithdrawal[] | undefined;
}

/**
 * Provides profile data to child components
 * Manages initial server-side data and client-side updates
 */
const ProfileContext = createContext<ProfileContextType | undefined>(undefined);
export function ProfileProvider({
  user: initialUser,
  paymentMethods: initialPaymentMethods,
  purchasedBatches: initialPurchasedBatches,
  referrals: initialReferrals,
  withdrawals: initialWithdrawals,
  children
}: ProfileContextType & {
  children: React.ReactNode;
}) {
  /** Manage profile data with React Query hook set the initial data from serverside */
  const {
    user,
    paymentMethods,
    purchasedBatches,
    referrals,
    withdrawals
  } = useProfileData({
    user: initialUser as User,
    paymentMethods: initialPaymentMethods as PaymentMethod,
    purchasedBatches: {
      batches: initialPurchasedBatches
    } as {
      batches?: Batch[];
    },
    referrals: initialReferrals,
    withdrawals: initialWithdrawals
  });
  return <ProfileContext value={{
    user,
    paymentMethods,
    purchasedBatches,
    referrals,
    withdrawals
  }} data-sentry-element="ProfileContext" data-sentry-component="ProfileProvider" data-sentry-source-file="profile-provider.tsx">
      {children}
    </ProfileContext>;
}
;

/**
 * Hook to access profile context
 * @returns Profile context data
 * @throws Error if used outside ProfileProvider
 */
export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('use Profile  must be used within a ProfileProvider');
  }
  return context;
};